import React, { useRef, useState } from "react";
import { Upload, Image as ImageIcon, X, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Drag-and-drop image uploader for the admin panel.
 * - Uploads to POST /api/admin/upload (multipart)
 * - On success, calls onChange(publicUrl) with the served URL
 * - Also supports pasting an external URL manually
 */
export default function ImageUploader({ value, onChange, label = "Image", testIdPrefix = "image" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max size 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Store the full public URL (relative /api/files/... → prefix with BACKEND_URL)
      const url = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    upload(f);
  };

  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-brand-ink">{label}</label>
      <div className="mt-1.5 flex gap-3">
        {/* Preview */}
        <div className="w-24 h-24 rounded-2xl overflow-hidden border border-brand-line bg-brand-pinkSoft grid place-items-center shrink-0 relative">
          {value ? (
            <>
              <img src={value} alt="" className="w-full h-full object-contain" />
              <button
                type="button"
                onClick={() => onChange("")}
                data-testid={`${testIdPrefix}-clear`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 grid place-items-center hover:bg-white text-brand-ink"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          ) : (
            <ImageIcon className="w-6 h-6 text-brand-stoneLight" />
          )}
        </div>

        {/* Drop zone + URL input */}
        <div className="flex-1 flex flex-col gap-2">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testIdPrefix}-dropzone`}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-3 text-center text-xs transition ${
              dragging ? "border-brand-red bg-brand-pinkLight" : "border-brand-line hover:border-brand-red bg-white"
            }`}
          >
            {uploading ? (
              <span className="inline-flex items-center gap-2 text-brand-red font-semibold"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>
            ) : (
              <span className="inline-flex items-center gap-2 text-brand-stone"><Upload className="w-3 h-3" /> Drag & drop or click to upload</span>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
              data-testid={`${testIdPrefix}-file-input`}
            />
          </div>
          <input
            type="url"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste an image URL"
            data-testid={`${testIdPrefix}-url-input`}
            className="h-10 rounded-xl bg-white border border-brand-line px-3 text-xs focus:outline-none focus:ring-2 focus:ring-brand-red"
          />
        </div>
      </div>
    </div>
  );
}
